import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import { LandingHero, TestimonialBand, FaqSection, CrossSell, FinalCta, localBusinessJsonLd } from "@/components/LandingBits";
import { trackCta, trackResetCtaClick } from "@/lib/analytics";

const PATH = "/group-personal-training-windermere";
const RESET = "/30-day-trial";

const PROBLEMS = [
  "You've joined gyms before but stopped going.",
  "You don't know exactly what to do when you train.",
  "You want to get stronger but don't want to train in an intimidating gym.",
  "You want someone to keep you accountable.",
  "Your body doesn't feel the same as it did in your 20s.",
  "You want to feel fit, capable and confident again.",
];

const INCLUDED = [
  { t: "Two coached Strength sessions a week", d: "Monday & Wednesday or Tuesday & Thursday, at 6am, 7am or 8am. Structured, small-group and coached — never a class you're left to follow along with." },
  { t: "Structured programming", d: "A clear plan that progresses week to week. You always know what you're doing and why." },
  { t: "Coaching and adjustments", d: "Exercises adapted to your body, your history and your goals — on the day, in the room." },
  { t: "Progress reviews", d: "Regular check-ins so you can see what's changing and what to work on next." },
  { t: "Accountability", d: "Coaches who notice when you're not there and a group who expect to see you." },
  { t: "Strength, movement & long-term health", d: "Guidance built around the second half of life: strength you keep, joints that move, energy that lasts." },
  { t: "Additional KORE training", d: "Access to the wider KORE timetable and gym floor where currently included in your membership." },
  { t: "A supportive community", d: "People in their 40s, 50s and 60s who are there for the same reasons you are." },
  { t: "A clear starting process", d: "A consultation, a sensible first 30 days and no guesswork about where to begin." },
];

const WHO = [
  "want to get back in shape",
  "want to become stronger",
  "want to improve fitness",
  "want more energy",
  "have struggled with consistency",
  "dislike big commercial gyms",
  "want coaching without traditional 1-to-1 personal training",
  "want to train around people like them",
];

const STEPS = [
  { n: "01", t: "Start", d: "Complete an initial consultation and onboarding so we understand where you are and where you want to be." },
  { n: "02", t: "Train", d: "Attend your structured weekly coaching sessions. Small group, expert coaching, a plan that fits you." },
  { n: "03", t: "Progress", d: "Build strength, fitness, confidence and consistency over time — and actually keep it." },
];

const FAQS = [
  { q: "What fitness level do I need?", a: "None in particular. Most members start after a long time away from training. Every exercise is scaled to you, and the first few weeks are about building a base, not testing you." },
  { q: "What age are your members?", a: "KORE is built for people 40+, and most members are in their 40s, 50s and 60s. It's a marketing focus rather than a rule — you're welcome whatever your age if the approach suits you." },
  { q: "What happens if I have an injury or limitation?", a: "Tell us at your consultation. Coaching in a small group means exercises are adjusted for you on the day — that's the point of coaching rather than a class." },
  { q: "Is this a fitness class?", a: "No. Sessions follow a structured programme and you're coached individually within a small group. You'll be taught what to do, how to do it and how to progress it." },
  { q: "How many people train together?", a: "Small groups — small enough that your coach can watch, adjust and correct throughout the session. You train twice a week, on either Monday & Wednesday or Tuesday & Thursday. Contact us if you'd like to know the current group sizes." },
  { q: "Do I need previous gym experience?", a: "No. Complete beginners are welcome and common. If you can walk in the door, we can coach you from there." },
  { q: "What happens when I first join?", a: "You'll start with a consultation, then the 30 Day Coached Trial — a month of coached sessions to get you moving well and into a routine. After that you decide whether to continue." },
  { q: "Where is KORE?", a: "Rowan House, Beresford Road, Bowness-on-Windermere, Cumbria LA23 2JG — a couple of minutes from the lake, with parking." },
];

export default function GroupCoaching() {
  const [tests, setTests] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {}); }, []);

  const resetClick = (loc) => () => trackResetCtaClick({ cta_text: "Start your 30 Day Trial", destination_url: RESET, cta_location: loc });

  return (
    <div data-testid="group-coaching-page">
      <Seo
        title="Group Personal Training Windermere"
        description="Small group personal training in Bowness-on-Windermere for adults 40+ who want expert coaching, structure, accountability and long-term results."
        path={PATH}
        jsonLd={localBusinessJsonLd({ url: `https://korewellbeing.com${PATH}`, description: "Small group personal training for people 40+ in Bowness-on-Windermere, Cumbria." })}
      />

      <LandingHero
        testId="coaching-hero"
        eyebrow="KORE Group Coaching · Bowness-on-Windermere"
        h1="Group Personal Training in Windermere for People 40+"
        sub="Get stronger, fitter and healthier with expert coaching, a clear plan and a small group of people who are there for the same reason you are."
        images={[
          { src: "/gallery/community-mixed.webp", alt: "KORE members together after a coached small-group session in Bowness-on-Windermere" },
          { src: "/gallery/kore-training-2.webp", alt: "KORE members with coach after a Kombat session" },
          { src: "/gallery/community-women.webp", alt: "KORE members training together on the gym floor" },
          { src: "/gallery/kore-training-1.webp", alt: "The KORE training floor in Bowness-on-Windermere" },
        ]}
        primary={{ to: RESET, label: "Start your 30 Day Trial", onClick: resetClick("hero") }}
        secondary={{ to: "/contact", label: "Book a Consultation", onClick: () => trackCta({ cta_name: "coaching_book_consultation", cta_text: "Book a Consultation", destination_url: "/contact", cta_location: "hero" }) }}
      />

      <SectionReveal as="section" data-testid="coaching-problems" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <span className="eyebrow">Sound familiar?</span>
            <h2 className="display-2 mt-4 text-kore-forest">Not a class. <span className="italic text-kore-gold">Coaching.</span></h2>
            <p className="mt-6 text-kore-ink/75 leading-relaxed text-[17px]">KORE Group Coaching isn't another fitness class. It's structured, coached training for people who want a plan, a coach and a reason to keep turning up.</p>
          </div>
          <ul className="md:col-span-7 space-y-5">
            {PROBLEMS.map((p) => (
              <li key={p} className="font-display text-2xl md:text-3xl text-kore-forest leading-tight border-b border-kore-gold/20 pb-5">{p}</li>
            ))}
          </ul>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="coaching-video" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore max-w-6xl grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <figure data-testid="coaching-video-frame" className="lg:col-span-5 mx-auto w-full max-w-[340px] aspect-[9/16] rounded-2xl overflow-hidden bg-kore-stone">
            <video
              data-testid="coaching-video-el"
              src="/gallery/typical-week.mp4"
              poster="/gallery/typical-week-poster.webp"
              controls muted playsInline preload="metadata"
              className="w-full h-full object-cover block bg-kore-forest"
              aria-label="A typical coached training week at KORE Wellbeing"
            />
          </figure>
          <div className="lg:col-span-7">
            <span className="eyebrow">See a real session</span>
            <h2 className="display-2 mt-4 text-kore-forest">This is what a coached week <span className="italic text-kore-gold">actually looks like.</span></h2>
            <p className="mt-6 text-base md:text-lg text-kore-ink/80 leading-[1.6] max-w-xl">Real members, real sessions — filmed on the training floor in Bowness-on-Windermere. No performers, no posing. Just people 40+ getting stronger together.</p>
          </div>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="coaching-included" className="bg-kore-forest text-kore-warm py-28 md:py-36">
        <div className="container-kore">
          <div className="max-w-2xl mb-16">
            <span className="eyebrow text-kore-light">What members receive</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-[-0.015em]">Coaching that does the thinking <span className="italic text-kore-light">so you can do the training.</span></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-x-12 gap-y-12">
            {INCLUDED.map((it) => (
              <div key={it.t}>
                <h3 className="font-display text-2xl leading-tight">{it.t}</h3>
                <p className="mt-3 text-kore-warm/70 leading-relaxed text-[15px]">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionReveal>

      <SectionReveal as="section" data-testid="coaching-who" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-lift">
              <img src="/gallery/community-mixed.webp" alt="KORE members in their 40s, 50s and 60s training together in Bowness-on-Windermere" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <span className="eyebrow">Who KORE Coaching is for</span>
            <h2 className="display-2 mt-4 text-kore-forest">People in their 40s, 50s and 60s who…</h2>
            <ul className="mt-8 space-y-3 text-[17px] text-kore-ink/80 leading-relaxed">
              {WHO.map((w) => <li key={w} className="flex gap-3"><span className="text-kore-gold">—</span>{w}</li>)}
            </ul>
            <p className="mt-8 text-kore-forest font-display italic text-xl">Training designed for the second half of life.</p>
            <p className="mt-6 text-sm text-kore-ink/70">Prefer a women-only start? <Link to="/womens-strength-foundations" data-testid="coaching-womens-link" className="text-kore-forest border-b border-kore-gold pb-0.5">Join the Women's Strength Foundations waiting list</Link>.</p>
          </div>
        </div>
      </SectionReveal>

      <TestimonialBand testId="coaching-testimonials" quotes={tests} />

      <SectionReveal as="section" data-testid="coaching-how" className="bg-kore-warm py-28 md:py-36">
        <div className="container-kore">
          <div className="max-w-2xl mb-16">
            <span className="eyebrow">How it works</span>
            <h2 className="display-2 mt-4 text-kore-forest">Three simple steps.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-x-14 gap-y-12">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div className="text-kore-gold text-sm font-medium">{s.n}</div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl text-kore-forest leading-tight">{s.t}</h3>
                <p className="mt-3 text-kore-ink/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-16">
            <Link to={RESET} data-testid="coaching-how-cta" onClick={resetClick("body")} className="btn-primary">Start your 30 Day Trial <ArrowRight size={14} /></Link>
          </div>
        </div>
      </SectionReveal>

      <section data-testid="coaching-gallery" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            ["/gallery/kore-training-2.webp", "Coached strength training on the KORE gym floor"],
            ["/gallery/community-women.webp", "KORE members training together in Bowness-on-Windermere"],
            ["/gallery/community-kombat.webp", "KORE members after a coached session"],
            ["/gallery/community-social.webp", "The KORE community at a members' social"],
          ].map(([src, alt]) => (
            <div key={src} className="rounded-3xl overflow-hidden aspect-[4/5] shadow-soft"><img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" /></div>
          ))}
        </div>
      </section>

      <FaqSection testId="coaching-faq" h2="Questions people ask before joining." faqs={FAQS} />

      <CrossSell
        testId="coaching-crosssell"
        eyebrow="Prefer to train on your own?"
        h2="KORE Access is our independent gym membership."
        body="If you already know how to train and just want a quieter, friendlier gym in Bowness-on-Windermere, KORE Access may suit you better."
        to="/gym-windermere"
        label="Explore The Gym"
        onClick={() => trackCta({ cta_name: "coaching_to_gym", cta_text: "Explore The Gym", destination_url: "/gym-windermere", cta_location: "body" })}
      />

      <FinalCta
        testId="coaching-final"
        h2={<>You don't need another gym membership.<br /><span className="italic text-kore-light">You need a plan you'll actually stick to.</span></>}
        primary={{ to: RESET, label: "Start your 30 Day Trial", onClick: resetClick("footer") }}
        secondary={{ to: "/contact", label: "Book a Consultation" }}
        note="Built for people 40+ · Rowan House, Bowness-on-Windermere"
      />
    </div>
  );
}
